import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CreateDepot } from "../components/fleet/CreateDepot";
import { DepotView } from "../components/fleet/DepotView";
import { EditDepot } from "../components/fleet/EditDepot";
import { EnergyCell } from "../components/fleet/EnergyCell";
import { UsageWindowPicker } from "../components/fleet/UsageWindowPicker";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice, SkeletonRows } from "../components/ui/Feedback";
import { RowEditButton } from "../components/ui/RowEditButton";
import { Cell, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric } from "../components/ui/Text";
import { getPage } from "../lib/api";
import { count, kwh } from "../lib/format";
import { useSession } from "../lib/session";
import type { Depot } from "../lib/types";
import { readUsageWindow, reportingZone } from "../lib/usageWindow";

/**
 * Depots — the physical sites a charge point belongs to.
 *
 * ## Why this page exists at all
 *
 * A depot is not decoration around a station. Its timezone is where that station's
 * allowance months and weeks begin (spec 6.1), and its unknown-tag policy decides
 * whether a card nobody has seen before gets enrolled or refused. Both are site
 * decisions with consequences for numbers an operator reports, so they get a page
 * where they can be read at a glance rather than a subform on the way to something
 * else.
 *
 * ## What can be edited, and what cannot
 *
 * The name, and a site that still enrols unseen cards can be stopped — never
 * restarted, because unseen cards are no longer enrolled anywhere. The timezone
 * stays fixed: moving it would move period boundaries that energy figures have
 * already been reported against. And there is no delete, since a depot cannot be
 * dropped while any charge point references it. A UI offering buttons the API
 * refuses would be worse than one that explains the model.
 *
 * ## Actions
 *
 * A row opens a read-only view of the site (`?depot=<id>`, so the back button
 * closes it); the pencil at the row's end, or Edit in the view, opens the edit
 * modal; New depot opens the create modal. Nothing edits inline above the table.
 *
 * ## The energy column is the fleet's only unpaginated total
 *
 * `/depots` returns every site, so the figures in this column add up to the whole
 * fleet's consumption in the window — which the charge-points table cannot claim,
 * because it sums one page. That makes this the table to read a monthly number
 * off, and the reason the window control is here as well as there.
 */

const COLUMNS: readonly Column[] = [
  { key: "name", label: "Site", width: "30%" },
  { key: "timezone", label: "Timezone", width: "20%" },
  { key: "provider", label: "Payment provider", width: "22%" },
  { key: "id", label: "Id" },
];

/** Appended only for an operator who may edit. Empty label: the icon names itself. */
const ACTIONS_COLUMN: Column = { key: "actions", label: "", width: "3rem" };

/** Appended only while a window is selected. See the note in `Fleet.tsx`. */
const ENERGY_COLUMN: Column = { key: "energy", label: "Energy", numeric: true, width: "13%" };

export function Depots() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  // The depot open in the view modal. In the URL, so a link to it is shareable.
  const viewingId = params.get("depot") ?? "";
  const range = params.get("range") ?? "";
  const fromDate = params.get("fromDate");
  const fromTime = params.get("fromTime");
  const toDate = params.get("toDate");
  const toTime = params.get("toTime");

  /**
   * Fetched once without a window so the zones are known before the window is
   * drawn in one of them. The circularity is real — the boundaries depend on the
   * sites, and the sites' totals depend on the boundaries — and this is the cheap
   * way out of it: the list is tens of rows and is already cached for the fleet
   * page's depot filter.
   */
  const sites = useQuery({
    queryKey: ["depots"],
    queryFn: () => getPage<Depot>("/depots"),
  });

  const zone = reportingZone(sites.data?.rows ?? [], "");
  const windowState = useMemo(
    // Memoised because resolving a preset reads the clock; see the note in `Fleet.tsx`.
    () => readUsageWindow(range, { fromDate, fromTime, toDate, toTime }, zone),
    [range, fromDate, fromTime, toDate, toTime, zone],
  );

  /** Resolved only. A custom range mid-typing keeps the plain list. See `Fleet.tsx`. */
  const usageWindow = windowState.kind === "resolved" ? windowState.window : undefined;

  const from = usageWindow?.from.toISOString() ?? "";
  const to = usageWindow?.to.toISOString() ?? "";

  const depots = useQuery({
    queryKey: ["depots", { from, to }],
    queryFn: () => getPage<Depot>("/depots", { query: { from, to } }),
    enabled: usageWindow !== undefined,
  });

  // The windowed rows when there are any, the plain list otherwise. Same shape
  // either way, so nothing below has to know which query answered.
  const rows = (usageWindow === undefined ? sites.data?.rows : depots.data?.rows) ?? [];
  const isPending = usageWindow === undefined ? sites.isPending : depots.isPending;
  const failed = usageWindow === undefined ? sites : depots;
  // Either half of the edit form is reason to open it; each half checks its own.
  const canEdit = can("depots.update") || can("payment_providers.manage");
  const columns = [
    ...COLUMNS,
    ...(usageWindow === undefined ? [] : [ENERGY_COLUMN]),
    ...(canEdit ? [ACTIONS_COLUMN] : []),
  ];
  const editing = rows.find((row) => row.id === editingId);
  // From the plain list as well, so the view does not blank while a window loads.
  const viewing =
    rows.find((row) => row.id === viewingId) ??
    sites.data?.rows.find((row) => row.id === viewingId);

  function startCreating() {
    setNotice(undefined);
    setIsCreating(true);
  }

  function startEditing(depotId: string) {
    setNotice(undefined);
    setEditingId(depotId);
  }

  function setViewing(depotId: string) {
    const next = new URLSearchParams(params);
    if (depotId === "") next.delete("depot");
    else next.set("depot", depotId);
    setParams(next, { replace: depotId === "" });
  }

  const energyWh = rows.reduce((sum, row) => sum + (row.usage?.energyWh ?? 0), 0);
  const sessions = rows.reduce((sum, row) => sum + (row.usage?.closedSessionCount ?? 0), 0);

  /**
   * Named only when it is not the sites' own calendar. `reportingZone` answers UTC
   * for a fleet spanning zones, and a total labelled "August" against a month
   * none of its sites keeps is exactly the figure that ends up in a report
   * unqualified.
   */
  const zoneCaveat =
    usageWindow === undefined || rows.every((row) => row.timezone === usageWindow.zone)
      ? undefined
      : `Boundaries drawn in ${usageWindow.zone}; these sites keep ${new Set(rows.map((row) => row.timezone)).size} different calendars.`;

  /**
   * A batch, for the same reason as `Fleet`'s: the range picker writes both ends
   * at once, and two calls reading one `params` snapshot would lose the first.
   */
  function setWindowParam(updates: Readonly<Record<string, string>>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (value === "") next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Depots"
        metrics={
          <>
            <Metric label="Sites" value={count(rows.length)} />
            <Metric
              label="Timezones"
              value={count(new Set(rows.map((row) => row.timezone)).size)}
              hint="period boundaries"
            />
            {usageWindow === undefined ? null : (
              <Metric
                label="Energy"
                value={kwh(energyWh)}
                unit="kWh"
                // Every site, not a page: this is the whole fleet's consumption.
                hint={`${usageWindow.label} · ${count(sessions)} sessions`}
              />
            )}
          </>
        }
        lede="A site's timezone is where its allowance months and weeks begin, and cannot be changed once stations are reporting against it. Unseen cards are refused at every new site."
        actions={
          can("depots.create") ? (
            <Button variant="primary" onClick={startCreating}>
              New depot
            </Button>
          ) : undefined
        }
      />

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}

      <form
        onSubmit={(event) => event.preventDefault()}
        aria-label="Reporting window"
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <UsageWindowPicker
          range={range}
          custom={{ fromDate, fromTime, toDate, toTime }}
          state={windowState}
          zone={zone}
          onChange={setWindowParam}
        />
      </form>

      {/*
        Not a `Notice`: that component announces the result of an action in one of
        two tones, and this is neither an outcome nor a failure. It is a caption on
        a figure, and it belongs in the same voice as the one under the table.
      */}
      {zoneCaveat === undefined ? null : (
        <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
          {zoneCaveat}
        </p>
      )}

      {failed.isError ? (
        <ErrorNotice error={failed.error} onRetry={() => void failed.refetch()} />
      ) : null}

      <TableFrame>
        <TableHead columns={columns} />
        {isPending ? (
          <SkeletonRows rows={3} columns={columns.length} />
        ) : (
          <tbody>
            {rows.map((row, index) => (
              <TableRow key={row.id} index={index} onActivate={() => setViewing(row.id)}>
                <Cell>
                  <span className="font-medium text-ink">{row.name}</span>
                </Cell>
                <Cell muted>{row.timezone}</Cell>
                <Cell muted={row.paymentProvider === null}>
                  {row.paymentProvider?.label ?? "None — no payments"}
                </Cell>
                <Cell muted className="font-mono text-micro tracking-normal normal-case">
                  {row.id}
                </Cell>
                {usageWindow === undefined ? null : <EnergyCell usage={row.usage} />}
                {canEdit ? (
                  <Cell numeric>
                    <RowEditButton label={row.name} onEdit={() => startEditing(row.id)} />
                  </Cell>
                ) : null}
              </TableRow>
            ))}
          </tbody>
        )}
      </TableFrame>

      {!isPending && rows.length === 0 ? (
        <EmptyState
          title="No depots yet."
          detail="A charge point cannot be registered without one, because a station with no site has no timezone to resolve its allowance periods in."
          action={
            can("depots.create") ? (
              <Button variant="primary" onClick={startCreating}>
                Create the first depot
              </Button>
            ) : undefined
          }
        />
      ) : null}

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        Depots can be renamed but never deleted, and their timezone never moves. Stations are
        registered into one from{" "}
        <Link
          to="/fleet"
          className="text-signal underline decoration-1 underline-offset-4 hover:no-underline"
        >
          Charge points
        </Link>
        .
      </p>

      <DepotView
        depot={viewing}
        open={viewingId !== "" && editing === undefined}
        canEdit={canEdit}
        windowLabel={usageWindow?.label}
        onOpenChange={(open) => {
          if (!open) setViewing("");
        }}
        onEdit={(depot) => startEditing(depot.id)}
      />

      {editing === undefined ? null : (
        <EditDepot
          key={editing.id}
          depot={editing}
          open
          onSaved={(depot) => {
            setNotice(`${depot.name} saved.`);
            setEditingId(undefined);
          }}
          onProviderSaved={(message) => {
            setNotice(message);
            setEditingId(undefined);
          }}
          onCancel={() => setEditingId(undefined)}
        />
      )}

      <CreateDepot
        open={isCreating}
        onCreated={(depot) => {
          setNotice(`${depot.name} created. Register a station into it from Charge points.`);
          setIsCreating(false);
        }}
        onCancel={() => setIsCreating(false)}
      />
    </>
  );
}
